import type { ReactNode } from 'react';
import { ChevronLeft } from 'lucide-react';
import { Link } from 'react-router-dom';

export interface PageHeaderProps {
  /** Micro-label above the title — section or breadcrumb context. */
  eyebrow?: string;
  title: ReactNode;
  description?: string;
  /** Buttons, right-aligned; wraps under the title on a phone. */
  actions?: ReactNode;
  /** Renders a back link above the title. */
  backTo?: { to: string; label: string };
}

/** The heading block at the top of every screen, so they all start the same. */
export function PageHeader({ title, eyebrow, description, actions, backTo }: PageHeaderProps) {
  return (
    <header className="flex flex-row items-center justify-between gap-3 flex-wrap sm:flex-nowrap">
      <div className="min-w-0 flex-1">
        {backTo && (
          <Link
            to={backTo.to}
            className="mb-2 inline-flex items-center gap-1 font-medium text-sm text-ink-3 transition-colors hover:text-accent"
          >
            <ChevronLeft className="h-3.5 w-3.5" />
            {backTo.label}
          </Link>
        )}
        {eyebrow && <p className="lbl mb-1.5">{eyebrow}</p>}
        {title && <h1 className="text-xl sm:text-2xl font-extrabold tracking-tight text-slate-800 dark:text-white">{title}</h1>}
        {description && <p className="mt-1 text-sm text-ink-2">{description}</p>}
      </div>

      {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
    </header>
  );
}
